import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, Button, FormControl, InputLabel,
  Select, MenuItem, Grid, Typography, Alert,
  FormHelperText, IconButton
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RichTextEditor } from '../common/RichTextEditor';
import DropZone from '../common/DropZone';
import { generateSlug } from '../../utils/slugGenerator';
import { uploadSingleImage } from '../../services/upload.service';

const newsStatusEnum = z.enum(['DRAFT', 'PUBLISHED', 'HIDDEN', 'ARCHIVED']);

// Slug removed from schema — auto-generated
const newsSchema = z.object({
  slug: z.string().optional(), // Hidden, auto-generated
  title: z.string().min(1, 'Укажите заголовок'),
  contentHtml: z.string().optional(),
  // news.excerpt — varchar(500) в БД: более длинный текст падал не валидацией,
  // а ошибкой Prisma уже при сохранении.
  excerpt: z.string().max(500, 'Аннотация не более 500 символов').optional(),
  coverImageUrl: z.string().optional(), // Uploaded via DropZone
  // Date of the material itself (news.date_iso) — what the public site prints
  // and orders by. `<input type="date">` gives YYYY-MM-DD.
  dateIso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Укажите дату').or(z.literal('')).optional(),
  status: newsStatusEnum.default('DRAFT'),
});

type NewsFormValues = z.infer<typeof newsSchema>;

// What actually goes to the API: `dateIso: null` clears the date, an empty
// string would mean "not sent".
type NewsFormPayload = Omit<NewsFormValues, 'dateIso'> & {
  dateIso: string | null;
  images?: string[];
};

// Existing records hold up to 55 gallery images; the cap must not silently
// truncate what was loaded, so it only bounds what can be added.
const MAX_GALLERY_IMAGES = 100;

// GET /news/:id returns the gallery as NewsImage rows ({ id, imageUrl, ... }),
// while the API expects plain URLs on save. Echoing the rows back was what made
// every edit of a news item with photos fail validation.
// news.date_iso comes back as a full ISO timestamp ("2026-08-13T00:00:00.000Z")
// because it is a `date` column read as UTC midnight; the date input needs the
// bare day. Slicing in UTC keeps the stored day intact.
function toDateInputValue(value: unknown): string {
  if (typeof value !== 'string') return '';
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : '';
}

// Local date, not toISOString(): east of Greenwich the UTC day is still
// yesterday for the first hours of the morning.
function todayInputValue(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function toImageUrls(images: unknown): string[] {
  if (!Array.isArray(images)) return [];
  return images
    .map(image => (typeof image === 'string' ? image : (image as { imageUrl?: string })?.imageUrl))
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}

interface NewsFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: NewsFormPayload) => Promise<void>;
  initialData?: NewsFormValues & { images?: string[] } | null;
  loading: boolean;
  error?: string | null;
}

export function NewsForm({ open, onClose, onSubmit, initialData, loading, error }: NewsFormProps) {
  const [coverImageUploading, setCoverImageUploading] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm<NewsFormValues>({
    resolver: zodResolver(newsSchema),
    defaultValues: {
      slug: '',
      title: '',
      contentHtml: '',
      excerpt: '',
      coverImageUrl: '',
      dateIso: todayInputValue(),
      status: 'DRAFT',
    },
  });

  const titleValue = watch('title');
  const coverImageUrl = watch('coverImageUrl');

  // Auto-generate slug when title changes (only for new items)
  useEffect(() => {
    if (!initialData && titleValue && titleValue.trim().length > 0) {
      const autoSlug = generateSlug(titleValue);
      setValue('slug', autoSlug);
    }
  }, [titleValue, initialData, setValue]);

  useEffect(() => {
    if (initialData) {
      // Every one of these columns is nullable in the DB, and the zod schema
      // types them as strings: handing reset() a raw record put `null` into the
      // fields and every save of such a record failed with
      // "Expected string, received null" before the request was ever sent.
      reset({
        slug: initialData.slug ?? '',
        title: initialData.title ?? '',
        contentHtml: initialData.contentHtml ?? '',
        excerpt: initialData.excerpt ?? '',
        coverImageUrl: initialData.coverImageUrl ?? '',
        dateIso: toDateInputValue(initialData.dateIso),
        status: initialData.status ?? 'DRAFT',
      });
      setGalleryImages(toImageUrls(initialData.images));
    } else {
      reset({
        slug: '',
        title: '',
        contentHtml: '',
        excerpt: '',
        coverImageUrl: '',
        dateIso: todayInputValue(),
        status: 'DRAFT',
      });
      setGalleryImages([]);
    }
  }, [initialData, open, reset]);

  // Handle image upload via DropZone
  const handleImageUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setCoverImageUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setCoverImageUploading(false);
    }
  }, []);

  const handleCoverImageChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('coverImageUrl', url);
  }, [setValue]);

  // Handle gallery images upload
  const handleGalleryUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setGalleryUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setGalleryUploading(false);
    }
  }, []);

  const handleGalleryChange = useCallback((urls: string | string[]) => {
    const urlArray = Array.isArray(urls) ? urls : [urls];
    setGalleryImages(prev => {
      // Add new unique URLs
      const newUrls = urlArray.filter(url => !prev.includes(url));
      return [...prev, ...newUrls].slice(0, Math.max(prev.length, MAX_GALLERY_IMAGES));
    });
  }, []);

  const handleRemoveGalleryImage = useCallback((index: number) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFormSubmit = useCallback(async (data: NewsFormValues) => {
    await onSubmit({ ...data, dateIso: data.dateIso || null, images: galleryImages });
  }, [galleryImages, onSubmit]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать новость' : 'Создать новость'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Title — main field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заголовок *"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            
            {/* Date of the material — what the site shows and sorts by */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата материала"
                InputLabelProps={{ shrink: true }}
                error={!!errors.dateIso}
                helperText={errors.dateIso?.message || 'Дата, которая показывается на сайте'}
                {...register('dateIso')}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Статус *</InputLabel>
                <Select
                  label="Статус *"
                  {...register('status')}
                >
                  <MenuItem value="DRAFT">Черновик</MenuItem>
                  <MenuItem value="PUBLISHED">Опубликовано</MenuItem>
                  <MenuItem value="HIDDEN">Скрыто</MenuItem>
                  <MenuItem value="ARCHIVED">Архив</MenuItem>
                </Select>
                <FormHelperText>{errors.status?.message}</FormHelperText>
              </FormControl>
            </Grid>
            
            {/* Slug — hidden field, shown as read-only hint */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="URL (автоматически)"
                value={watch('slug') || ''}
                disabled
                helperText="Генерируется из заголовка"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    bgcolor: 'action.hover',
                  }
                }}
              />
            </Grid>
            
            {/* Cover Image — DropZone instead of URL TextField */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Изображение обложки
              </Typography>
              <DropZone
                onUpload={handleImageUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={coverImageUrl || ''}
                onChange={handleCoverImageChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {coverImageUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* Gallery Images — Multiple upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Фотогалерея
              </Typography>
              <DropZone
                onUpload={handleGalleryUpload}
                type="image"
                accept={{
                  'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
                }}
                maxFiles={10}
                maxSize={10}
                multiple={true}
                onChange={handleGalleryChange}
                label="Перетащите изображения или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB каждое, до 10 файлов за раз"
              />
              {galleryUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка изображений...
                </Typography>
              )}
              
              {/* Show uploaded gallery images */}
              {galleryImages.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {galleryImages.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 80, height: 80 }}>
                      <img 
                        src={url} 
                        alt={`Фото ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveGalleryImage(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                          width: 20,
                          height: 20,
                        }}
                      >
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
            
            {/* Excerpt */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Аннотация"
                multiline
                rows={3}
                error={!!errors.excerpt}
                helperText={errors.excerpt?.message}
                {...register('excerpt')}
              />
            </Grid>
            
            {/* Content — Rich Text Editor */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Содержание
              </Typography>
              <Controller
                name="contentHtml"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Введите текст новости..."
                    minHeight={300}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" disabled={loading || coverImageUploading || galleryUploading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}